import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { DataMainTemplate } from "@nocios/presentation-kit";
import Config from "src/Constants/Config";
import setCurrentPage from "src/Utils/setCurrentPage";
import Btn from "src/Component/Btn";

// Constants
const PAGE_ID = 1;
const BG_ID = 1;
const BG_TYPE = "jpg";

const LEFT_TITLE = "THE FUTURE OF TELCO OPERATIONS\nPOWERED BY AI & AUTOMATION";

// Image slider component
const ImageSlider = () => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const currentPage = useSelector((state: { appReducer: { currentPage: number } }) => state.appReducer.currentPage);

  useEffect(() => {
    setIsVisible(false); // Reset visibility when page changes
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [currentPage]); // This effect will run on currentPage changes

  const images = [1, 2, 3];
  const GAP = "calc(var(--width-73) * -1)";

  return (
    <div
      ref={containerRef}
      style={{
        position: "absolute",
        top: "50%",
        right: 0,
        transform: "translateY(-50%)",
        width: "60%",
        height: "auto",
        pointerEvents: "none",
        zIndex: 10,
        overflow: "hidden",
      }}
    >
      {images.map((index, i) => (
        <div
          key={index}
          style={{
            marginTop: i === 0 ? "var(--width-50)" : 0,
            marginBottom: i < images.length - 1 ? GAP : 0,
            width: "100%",
            opacity: isVisible ? 1 : 0,
            transform: `translateX(${isVisible ? 0 : "100%"})`,
            transition: `opacity 0.7s ease-out, transform 0.7s ease-out`,
            transitionDelay: `${i * 0.3}s`,
          }}
        >
          <img
            src={`${Config.BASE_URL}/images/screen_${PAGE_ID}/right_${index}.png`}
            alt={`Presentation Data ${index}`}
            style={{
              width: "75%",
              height: "auto",
              display: "block",
              animation: `glow 3s ease-in-out infinite`,
            }}
          />
        </div>
      ))}
    </div>
  );
};

// Main data function that returns the template data
const DATA = (): DataMainTemplate => {
  const currentPage = useSelector((state: { appReducer: { currentPage: number } }) => state.appReducer.currentPage);

  const baseImageBG = `${Config.BASE_URL}/images/BG/`;

  // Navigation handler
  const handleButtonClick = (pageId: number | null) => {
    if (pageId !== null) {
      setCurrentPage(pageId);
    }
  };

  // Define keyframes for animations
  const keyframes = `
    @keyframes gradientMove {
      0% {
        background-position: 100% 50%;
      }
      100% {
        background-position: 0% 50%;
      }
    }

    @keyframes pulse {
      0% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.03);
      }
      100% {
        transform: scale(1);
      }
    }
    @keyframes shadowPulse {
      0% {
        box-shadow: 0 0 50px rgba(255,255,255, 0.3)
      }
      50% {
        box-shadow: 0 0 55px rgba(255,255,255,0.4);
      }
      100% {
        box-shadow: 0 0 50px rgba(255,255,255, 0.3);
      }
    }
    @keyframes glow {
      0% { filter: drop-shadow(0 0 1px rgba(125,208,255,0.1)); }
      50% { filter: drop-shadow(0 0 10px rgba(125,208,255,0.5)); }
      100% { filter: drop-shadow(0 0 1px rgba(125,208,255,0.1)); }
    }
  `;

  const leftAfterDescription = (
    <div style={{ marginTop: "var(--width-60)" }}>
      <style>{keyframes}</style>
      <Btn
        label="SEE OUR PLATFORM"
        color="purple"
        onClick={() => handleButtonClick(2)}
        icon={
          <img
            src={`${Config.BASE_URL}/images/icons/product.png`}
            style={{
              width: "var(--width-28)",
              height: "auto",
            }}
            alt="product"
          />
        }
        style={{
          gap: "var(--width-6)",
          backgroundSize: "200% 100%",
          animation: "gradientMove 1.5s linear alternate-reverse infinite, pulse 3s linear infinite, shadowPulse 3s linear infinite",
        }}
      />
    </div>
  );

  return {
    bgVideo: {
      poster: `${baseImageBG}BG_${BG_ID}.${BG_TYPE}`,
      video: `${Config.BASE_URL}/videos/bg_${PAGE_ID}.mp4`,
    },
    gradient:
      "linear-gradient(270deg, rgba(21, 50, 121, 0.00) 11.77%, #153279 87.81%), linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.60) 100%)",
    title: LEFT_TITLE,
    description:
      "Telco providers face growing challenges-delays,\ninefficiencies, and operational complexities.\n\nInfosys developed Telco Insights Solution powered by\nautonomous agents to provide <b>'single pane of glass’</b>\n across business systems enabling proactive insights-driven\ncustomer experience.",
    leftAfterDescription,
    rightSideComponent: <ImageSlider key={currentPage} />,
  };
};

export default DATA;
